// +----------------------------------------------------------------------
// | 全局布局：桌面=顶部横排导航（+头像下拉）；移动端(≤768px)=顶部 logo + 底部固定 Tab 栏
// | Tab 图标全部用内联 SVG，不依赖外部 CSS，任何手机内核都能正常显示
// +----------------------------------------------------------------------
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import IosGuide from '../IosGuide';
import './Layout.less';

// ------------------------------ Tab 图标（描线风格，跟随文字颜色） ------------------------------
const IconStroke = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconHome = () => (
  <IconStroke>
    <path d="M3.5 10.6 12 3.6l8.5 7V20a1 1 0 0 1-1 1h-5.2v-6.2H9.7V21H4.5a1 1 0 0 1-1-1z" />
  </IconStroke>
);

const IconChat = () => (
  <IconStroke>
    <path d="M20 4.6H4a.9.9 0 0 0-.9.9v10.2a.9.9 0 0 0 .9.9h4.3v4l4.6-4h7.1a.9.9 0 0 0 .9-.9V5.5a.9.9 0 0 0-.9-.9z" />
  </IconStroke>
);

const IconFlame = () => (
  <IconStroke>
    <path d="M12 3.2s5.2 4.7 5.2 9.2a5.2 5.2 0 0 1-10.4 0c0-1.9.9-3.7 1.9-5.2.3 1.1.9 2 1.8 2.6.3-2.4 1-4.8 1.5-6.6z" />
  </IconStroke>
);

const IconCheck = () => (
  <IconStroke>
    <circle cx="12" cy="12" r="8.4" />
    <path d="m8.4 12.3 2.4 2.4 4.8-5" />
  </IconStroke>
);

const IconMe = () => (
  <IconStroke>
    <circle cx="12" cy="8.2" r="3.6" />
    <path d="M4.9 20c.8-3.6 3.7-5.6 7.1-5.6s6.3 2 7.1 5.6" />
  </IconStroke>
);

export default function Layout({ children }) {
  const { user, isLoggedIn, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false); // 头像下拉
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const pathname = location.pathname;

  // 换页收起头像菜单
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // 点外面收起头像菜单
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/');
  };

  const nickname = (user && (user.nickname || user.username)) || '';
  const initial = nickname ? nickname.slice(0, 1).toUpperCase() : '';

  // 子页面（/decision/*、/profile/edit、/topics/:key、/vip、/free-code）：
  // 主顶栏照常显示，左边多个返回钮（手机端），中间加页面标题，不显示「想想」字标。
  // /chat → layout--chat（锁视口，聊天输入框刚需，自带藏顶栏规则）。
  // 原 layout--imm 沉浸模式已下线（2026-09-29 子页顶栏大统一：/vip、/free-code
  // 的自绘吸顶顶栏撤掉，全部吃主顶栏），/decision/share/:token 是独立公开页不进
  // Layout，不受影响。两个 class 互不叠加：imm 的 padding-bottom 会盖掉 chat 的
  // padding-bottom: 0 —— 现在 imm 没有使用者了，这条只作历史备注。
  const isDecisionSub = pathname.startsWith('/decision');
  const isProfileEdit = pathname.startsWith('/profile/edit');
  const isTopicDetail = /^\/topics\/.+/.test(pathname); // /topics 列表页不算子页
  const isVip = pathname.startsWith('/vip');
  const isFreeCode = pathname.startsWith('/free-code');
  const showBack = isDecisionSub || isProfileEdit || isTopicDetail || isVip || isFreeCode;
  const subTitle = isDecisionSub
    ? '决定详情'
    : isProfileEdit
      ? '编辑资料'
      : isTopicDetail
        ? '话题详情'
        : isVip
          ? '想想会员'
          : isFreeCode
            ? '领取体验码'
            : '';
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else if (isProfileEdit) navigate('/profile');
    else if (isVip) navigate('/profile');
    else navigate('/');
  };

  // 底部 Tab（「我的」未登录时跳登录页）
  // 注：聊天页已做成独立子页（手机端整屏、不吃 Tab 栏），入口统一走首页的
  //     整宽「和想想聊聊」按钮，所以这里不再占 Tab 位。要恢复取消注释即可。
  const TABS = [
    { key: 'home', label: '首页', Icon: IconHome, href: '/', match: (p) => p === '/' },
    // { key: 'chat', label: '和想想聊', Icon: IconChat, href: '/chat', match: (p) => p.startsWith('/chat') },
    { key: 'topics', label: '大家在纠结', Icon: IconFlame, href: '/topics', match: (p) => p.startsWith('/topics') },
    { key: 'history', label: '我的决定', Icon: IconCheck, href: '/history', match: (p) => p.startsWith('/history') },
    {
      key: 'me',
      label: '我的',
      Icon: IconMe,
      href: isLoggedIn ? '/profile' : '/login',
      match: (p) => ['/profile', '/memories', '/settings', '/login'].some((x) => p.startsWith(x)),
    },
  ];

  return (
    <div
      className={`layout${
        pathname.startsWith('/chat') ? ' layout--chat' : ''
      }`}
    >
      <header className="layout-header">
        <div className="layout-header-inner">
          <div className="layout-left">
            {showBack && (
              <button type="button" className="layout-back" onClick={goBack} aria-label="返回上一页">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M14.5 5.5 8 12l6.5 6.5" />
                </svg>
              </button>
            )}
            {/* 决定子页只留返回钮，不显示「想想」字标（用户要求去掉） */}
            {!showBack && (
              <Link to="/" className="layout-logo">
                想想
              </Link>
            )}
          </div>

          {/* 子页标题：顶栏正中（手机端），提示当前在哪个页面 */}
          {showBack && subTitle && <h1 className="layout-sub-title">{subTitle}</h1>}

          <div className="layout-right" ref={menuRef}>
            <nav className="layout-nav">
              {[
                { to: '/chat', label: '和想想聊' },
                { to: '/topics', label: '大家在纠结' },
                { to: '/history', label: '我的决定' },
              ].map((item) => (
                <Link key={item.to} to={item.to} className="layout-nav-item">
                  {item.label}
                </Link>
              ))}

              {isLoggedIn ? null : (
                <Link to="/login" className="layout-nav-item layout-nav-login">
                  登录
                </Link>
              )}
            </nav>

            {isLoggedIn && (
              <div className="layout-user">
                <button
                  type="button"
                  className="layout-avatar"
                  onClick={() => setMenuOpen((v) => !v)}
                  title={nickname}
                >
                  {user && user.avatar ? (
                    <img src={user.avatar} alt={nickname} />
                  ) : (
                    <span>{initial}</span>
                  )}
                </button>

                {menuOpen && (
                  <div className="layout-menu">
                    <div className="layout-menu-head">
                      <strong>{nickname}</strong>
                      <span className="muted">@{user.username}</span>
                    </div>
                    <Link to="/profile" className="layout-menu-item">
                      我的资料
                    </Link>
                    <Link to="/memories" className="layout-menu-item">
                      AI 记住的我
                    </Link>
                    <Link to="/settings" className="layout-menu-item">
                      偏好设置
                    </Link>
                    <button type="button" className="layout-menu-item" onClick={handleLogout}>
                      退出登录
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="layout-main">{children}</main>

      {/* 移动端底部固定 Tab 栏（≤768px 由 CSS 控制显示） */}
      <nav className="layout-tabbar">
        {TABS.map(({ key, label, Icon, href, match }) => (
          <Link
            key={key}
            to={href}
            className={`layout-tab${match(pathname) ? ' is-active' : ''}`}
          >
            <Icon />
            <span>{label}</span>
          </Link>
        ))}
      </nav>
      {/* iOS Safari「添加到主屏幕」引导（内部自己判断 iOS/已添加/?mb=1，桌面上不渲染） */}
      <IosGuide />
    </div>
  );
}
